import { useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import Table from "react-bootstrap/Table"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"
import { DateTime } from "luxon"

import useT from "utils/hooks/useT"
import "components/Lims/LimsCard.scss"
import { limsAudit } from "state_management"
import { auditObjectLabel, auditSampleLabel } from "components/Lims/auditObjectLabel"

// AuditSection (SPEC-034 / F2) — a self-contained, collapsible "Audit trail" section under the QC
// results for ONE analysis. It shows the immutable audit entries SCOPED to this analysis (the run
// itself plus its samples/results/SST/OOS/reviews/qaDecisions/reruns), resolved server-side
// (SPEC-017) so the analyst doesn't have to sift the whole team's global trail in the AuditViewer.
//
// LAZY: nothing is fetched on mount. The trail is fetched the FIRST time the section is expanded
// (RR3 lesson — panel-mount fetches are costly), and kept in a SEPARATE redux key
// (limsAudit.byAnalysis[analysisId]) so it never collides with / overwrites the global AuditViewer
// trail. Re-expanding does not re-fetch; the entries are read-only (the trail is append-only, GMP).
//
// The table/label rendering deliberately MIRRORS the global AuditViewer (same columns, same
// objectType labels, same timestamp formatting) for a consistent read — AuditViewer itself is left
// untouched.
export default function AuditSection({ analysisId }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  // Whether the lazy fetch has been kicked off once (so re-collapsing/expanding doesn't refetch).
  const [fetched, setFetched] = useState(false)

  const entries = useSelector((state) => limsAudit.selectAuditForAnalysis(state, analysisId), shallowEqual)
  const loading = useSelector((state) => limsAudit.selectAuditForAnalysisLoading(state, analysisId))

  const toggle = () => {
    const next = !open
    setOpen(next)
    // Lazy-fetch on the FIRST expand only.
    if (next && !fetched && analysisId) {
      setFetched(true)
      dispatch(limsAudit.fetchForAnalysis(analysisId))
    }
  }

  if (!analysisId) return null

  return (
    <section className="ResultEntryPanel-audit LimsCard">
      {/* #4217: the shared collapsible card header (LimsCard), like the SST panel's. */}
      <button
        type="button"
        className="LimsCard-toggle"
        onClick={toggle}
        aria-expanded={open}
      >
        {open ? <FiChevronDown /> : <FiChevronRight />}
        <span className="ResultEntryPanel-audit-title">{t("limsAuditOnAnalysis")}</span>
        {open && !loading && (
          <span className="ResultEntryPanel-audit-count">
            {t("limsAuditOnAnalysisCount", { count: entries.length })}
          </span>
        )}
      </button>

      {open && (
        <div className="ResultEntryPanel-audit-body">
          <AuditBody loading={loading} entries={entries} t={t} />
        </div>
      )}
    </section>
  )
}

// The expanded body: a loading note, an empty note, or the read-only entry table (the same
// columns/formatting as the global AuditViewer). Split out so the section stays a flat conditional.
function AuditBody({ loading, entries, t }) {
  if (loading) return <p className="ResultEntryPanel-message">{t("loading")}</p>
  if (entries.length === 0) return <p className="ResultEntryPanel-message">{t("limsAuditOnAnalysisEmpty")}</p>
  return (
    <Table striped hover responsive className="ResultEntryPanel-audit-table">
      <thead>
        <tr>
          <th>{t("limsAuditTime")}</th>
          <th>{t("member")}</th>
          <th>{t("limsAuditAction")}</th>
          <th>{t("limsAuditObject")}</th>
          {/* #4138 — which lot the entry is about. `limsAuditObject` shows the ANALYSIS name
              for a result entry, so a three-lot tray produced three rows that read
              identically. Reuses the existing "Sample / lot" string.
              #4156 — and the lot now ALWAYS reads here, never in Objekt: both cells go
              through `auditObjectLabel` / `auditSampleLabel` so the two audit surfaces
              cannot drift. */}
          <th>{t("limsCoaSample")}</th>
          <th>{t("limsAuditReason")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, i) => (
          <tr key={entry.id || i}>
            <td>{entry.timestamp ? DateTime.fromISO(entry.timestamp).toFormat("yyyy-LL-dd HH:mm") : ""}</td>
            <td>{entry.actorInitials}</td>
            <td>{t(entry.action)}</td>
            <td>{auditObjectLabel(entry, t)}</td>
            <td>{auditSampleLabel(entry)}</td>
            <td>{entry.reason || ""}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

